export type PortalGateRoute = "noPortalAccess" | "staffLoginRequired";

export interface PortalGateInput {
	/** True once `/api/v1/me` answered; a transient failure leaves it false and must never gate. */
	meKnown: boolean;
	hasPortalAccess: boolean | null | undefined;
	staffLoginRequiresBroker: boolean;
	/** An invitation token waits in sessionStorage: acceptance runs first and decides the outcome. */
	invitationPending: boolean;
}

/** Route hash of each gate screen; must match the patterns in manifest.json. */
export const PORTAL_GATE_HASH: Record<PortalGateRoute, string> = {
	noPortalAccess: "no-portal-access",
	staffLoginRequired: "staff-login",
};

/**
 * Single source of the gate decision, used before router init (Component) and after invitation
 * acceptance (App). Order matters: staff password sign-in first, then the guards that keep a
 * legitimate user out of the gate (unknown /me, portal access, pending invitation).
 */
export function resolvePortalGate(input: PortalGateInput): PortalGateRoute | null {
	if (input.staffLoginRequiresBroker) {
		return "staffLoginRequired";
	}
	if (!input.meKnown) {
		return null;
	}
	if (input.hasPortalAccess === true) {
		return null;
	}
	if (input.invitationPending) {
		return null;
	}
	return "noPortalAccess";
}

/**
 * Hash to start the router with: the gate screen when gated, the dashboard when a non-gated user
 * opens a gate hash (bookmark, reload after a language switch), otherwise the current hash unchanged.
 */
export function resolveInitialHash(gate: PortalGateRoute | null, currentHash: string): string {
	const hash = currentHash.replace(/^#\/?/, "");
	if (gate) {
		return PORTAL_GATE_HASH[gate];
	}
	return Object.values(PORTAL_GATE_HASH).includes(hash) ? "" : hash;
}

/**
 * While gated, every hash other than the gate's own leads back to the gate. Returns the hash to
 * replace the current one with, or null when the router may parse the hash as it is.
 */
export function redirectWhileGated(gate: PortalGateRoute | null, newHash: string): string | null {
	if (!gate) {
		return null;
	}
	const gateHash = PORTAL_GATE_HASH[gate];
	return newHash === gateHash ? null : gateHash;
}
